import { useListCampaigns, type PaidPlanCode } from "@scalephant/contract/client";
import { isPaidPlanCode } from "@scalephant/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useMe } from "../features/auth/session";
import { BillingSettings, type CheckoutOutcome } from "../features/settings/BillingSettings";
import { useT } from "../i18n/I18nProvider";

interface BillingSearch {
  readonly checkout?: CheckoutOutcome;
  /** Stripe's checkout session id on the success return. */
  readonly session_id?: string;
  /** Set by a beta link's signup: open checkout for this plan right away. */
  readonly start?: PaidPlanCode;
}

export const Route = createFileRoute("/app/settings/billing")({
  validateSearch: (search: Record<string, unknown>): BillingSearch => ({
    ...(search.checkout === "success" || search.checkout === "canceled"
      ? { checkout: search.checkout }
      : {}),
    ...(typeof search.session_id === "string" && search.session_id !== ""
      ? { session_id: search.session_id }
      : {}),
    ...(typeof search.start === "string" && isPaidPlanCode(search.start)
      ? { start: search.start }
      : {}),
  }),
  component: BillingPage,
});

function BillingPage() {
  const t = useT();
  const me = useMe();
  const navigate = useNavigate();
  const { checkout, session_id: sessionId, start } = Route.useSearch();
  // A new customer back from checkout goes on to onboarding, as the dashboard would send
  // them: an organization without a campaign has not finished it.
  const [activated, setActivated] = useState(false);
  const campaigns = useListCampaigns({}, { query: { enabled: activated, retry: false } });
  const firstCampaignMissing = activated && campaigns.data?.campaigns.length === 0;
  useEffect(() => {
    if (firstCampaignMissing) {
      void navigate({ to: "/app/onboarding", search: { plan: "active" }, replace: true });
    }
  }, [firstCampaignMissing, navigate]);

  if (!me.data) return <p>{t("common.loading")}</p>;
  return (
    <>
      <h2>{t("settings.billing.title")}</h2>
      <BillingSettings
        key={`${me.data.activeOrganizationId ?? "none"}:${checkout ?? ""}`}
        me={me.data}
        checkoutOutcome={checkout}
        checkoutSessionId={sessionId}
        onActivated={() => {
          setActivated(true);
        }}
        autoCheckout={start}
      />
    </>
  );
}
